import React from 'react';
import {CircularProgress, Stack} from '@mui/material';
import {Icon, Label, designSystemToken} from '@lightricks/react-design-system';
import type {ConversationMessageAttachment} from '@/types/conversationMessage';
import styles from './MessageAttachments.module.scss';

function MessageAttachments({
  attachments,
  isLoading = false,
}: {
  attachments: ConversationMessageAttachment[];
  isLoading?: boolean;
}) {
  if (isLoading && !attachments.length) {
    return (
      <Stack className={styles.loadingContainer}>
        <CircularProgress size={20} />
      </Stack>
    );
  }
  if (!attachments.length) {
    return null;
  }
  return (
    <Stack className={styles.attachments}>
      {attachments.map((attachment) => {
        if (attachment.mimeType === 'image/gif') {
          return (
            <a
              key={attachment.id}
              href={attachment.viewableUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.attachmentVideoWrapper}
            >
              <img
                src={attachment.viewableUrl}
                alt={attachment.fileName}
                className={styles.attachmentImage}
              />
              <span className={styles.attachmentVideoIcon}>
                <Icon
                  name="Navigation-Gif"
                  size="medium"
                  appearance="neutral"
                  color={designSystemToken('semantic.fg.white')}
                />
              </span>
            </a>
          );
        }
        if (attachment.mimeType.startsWith('image/')) {
          return (
            <a
              key={attachment.id}
              href={attachment.viewableUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={attachment.viewableUrl}
                alt={attachment.fileName}
                className={styles.attachmentImage}
              />
            </a>
          );
        }
        if (attachment.mimeType.startsWith('video/')) {
          return (
            <a
              key={attachment.id}
              href={attachment.viewableUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.attachmentVideoWrapper}
            >
              <video
                src={attachment.viewableUrl}
                className={styles.attachmentVideo}
                autoPlay
                muted
                loop
                playsInline
              />
              <span className={styles.attachmentVideoIcon}>
                <Icon
                  name="Social-InstagramReels"
                  size="medium"
                  appearance="neutral"
                  color={designSystemToken('semantic.fg.white')}
                />
              </span>
            </a>
          );
        }
        const ext = attachment.fileName.split('.').pop()?.toUpperCase() ?? '';
        return (
          <a
            key={attachment.id}
            href={attachment.downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.attachmentFileCard}
          >
            <span className={styles.attachmentFileIcon}>
              <Icon
                name="Navigation-Page"
                size="large"
                appearance="neutral"
                color={designSystemToken('semantic.fg.secondary')}
              />
              {ext && (
                <span
                  className={
                    ext.length >= 4
                      ? styles.attachmentFileExtSmall
                      : styles.attachmentFileExt
                  }
                >
                  {ext}
                </span>
              )}
            </span>
            <Label size="sm" className={styles.attachmentFileName}>
              {attachment.fileName}
            </Label>
          </a>
        );
      })}
    </Stack>
  );
}

export default MessageAttachments;
